import {SerializedError} from '@reduxjs/toolkit';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {IErrorEventDataDefinition, IErrorEventDataSchema} from '@/services';

export type ErrorType =
  | IErrorEventDataDefinition
  | FetchBaseQueryError
  | SerializedError
  | undefined
  | null;

function hasErrorMessage(
  data: unknown,
): data is {message?: string; error?: string} {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('message' in data || 'error' in data) &&
    (typeof (data as {message?: string}).message === 'string' ||
      typeof (data as {error?: string}).error === 'string')
  );
}

const extractErrorDetails = (errorObj: {code?: string; message?: string}) => ({
  code: errorObj.code || '',
  message: errorObj.message || '',
});

/** Parse response of Modak Errors or default http errors */
const getErrorFromFetchError = (
  fetchError: FetchBaseQueryError,
): IErrorEventDataSchema => {
  const statusCode = fetchError.status.toString();
  const extra = fetchError.data
    ? typeof fetchError.data === 'string'
      ? fetchError.data
      : JSON.stringify(fetchError.data)
    : undefined;
  const errorsArray = (fetchError.data as {errors?: any[]})?.errors;
  const lastError = Array.isArray(errorsArray)
    ? errorsArray[errorsArray?.length - 1]
    : undefined;
  let message = extra;
  if (hasErrorMessage(fetchError.data)) {
    message = fetchError.data.message ?? fetchError.data.error;
  }
  return {
    error: {
      ...extractErrorDetails(
        lastError ?? {
          code: statusCode,
          message: message,
        },
      ),
      status: statusCode,
      extra: extra,
    },
    isModakError: lastError ? true : false,
  };
};

const getErrorFromSerializedError = (
  serializedError: SerializedError,
): IErrorEventDataSchema => {
  return {
    error: {
      code: serializedError.name || serializedError.code || 'SerializedError',
      message:
        serializedError.message || serializedError.stack || 'Unknown error',
      extra: JSON.stringify(serializedError),
    },
  };
};

export const getErrorSchema = (
  error: ErrorType,
): IErrorEventDataSchema | null => {
  if (!error) return null;

  if (typeof error === 'object' && 'status' in error && 'data' in error) {
    const fetchError: FetchBaseQueryError = error;
    return getErrorFromFetchError(fetchError);
  }

  if (typeof error === 'object' && 'name' in error && 'message' in error) {
    const serializedError: SerializedError = error;
    return getErrorFromSerializedError(serializedError);
  }

  if (typeof error === 'object' && 'code' in error && 'message' in error) {
    const iError = error as IErrorEventDataDefinition;
    return {
      error: extractErrorDetails(iError),
    };
  }

  if (typeof error === 'string') {
    return {
      error: {
        code: 'STRING_ERROR',
        message: error,
      },
    };
  }

  if (error instanceof Error) {
    return {
      error: {
        code: error.name || 'Error',
        message: error.message || 'An error occurred',
        extra: error.stack,
      },
    };
  }

  return {
    error: {
      code: 'UNKNOWN_ERROR',
      message: 'Unknown error',
      extra: JSON.stringify(error),
    },
  };
};
